/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React from "react";
import { CalendarDays, X } from "lucide-react";

/* 客户创建时间筛选：快捷（今天 / 本周 / 本月 / 上月 / 近 7 / 30 天）+ 自定义起止日期；周从周一起算，按浏览器本地时区 */
export const CREATED_MODES = [
  { id: "", zh: "全部时间", en: "Any time" },
  { id: "today", zh: "今天新增", en: "Added today" },
  { id: "week", zh: "本周新增", en: "This week" },
  { id: "month", zh: "本月新增", en: "This month" },
  { id: "lastMonth", zh: "上月新增", en: "Last month" },
  { id: "7d", zh: "近 7 天", en: "Last 7 days" },
  { id: "30d", zh: "近 30 天", en: "Last 30 days" },
  { id: "custom", zh: "自定义时间段…", en: "Custom range…" },
];

/* 统一按公司时区（洛杉矶）计算「今天 / 本周 / 本月」与自定义日期边界，与服务端周报聚合一致，不受浏览器时区影响 */
export const APP_TZ = "America/Los_Angeles";
const DTF = new Intl.DateTimeFormat("en-US", { timeZone: APP_TZ, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", weekday: "short" });
const WD = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const tzParts = (date) => { const p = Object.fromEntries(DTF.formatToParts(date).map((x) => [x.type, x.value])); return { y: +p.year, m: +p.month - 1, d: +p.day, h: +p.hour, mi: +p.minute, s: +p.second, wd: WD[p.weekday] ?? 0 }; };
const tzOffsetMs = (date) => { const p = tzParts(date); return Date.UTC(p.y, p.m, p.d, p.h, p.mi, p.s) - Math.floor(date.getTime() / 1000) * 1000; };
/* 洛杉矶本地 y/m/d 零点 → UTC 毫秒（m 为 0 起、可越界，Date.UTC 自动归一化） */
export const tzMidnight = (y, m, d) => { const guess = Date.UTC(y, m, d); let t = guess - tzOffsetMs(new Date(guess)); const off2 = tzOffsetMs(new Date(t)); if (guess - off2 !== t) t = guess - off2; return t; };
export const tzToday = (now = new Date()) => tzParts(now);
export const tzDateStr = (utcMs) => { const p = tzParts(new Date(utcMs)); return `${p.y}-${String(p.m + 1).padStart(2, "0")}-${String(p.d).padStart(2, "0")}`; };

export function createdRange({ mode, from, to }, now = new Date()) {
  if (!mode) return null;
  const t = tzToday(now);
  const day = (k) => tzMidnight(t.y, t.m, t.d + k);
  if (mode === "today") return [day(0), Infinity];
  if (mode === "week") return [day(-((t.wd + 6) % 7)), Infinity];
  if (mode === "month") return [tzMidnight(t.y, t.m, 1), Infinity];
  if (mode === "lastMonth") return [tzMidnight(t.y, t.m - 1, 1), tzMidnight(t.y, t.m, 1)];
  if (mode === "quarter") return [tzMidnight(t.y, Math.floor(t.m / 3) * 3, 1), Infinity];
  if (mode === "year") return [tzMidnight(t.y, 0, 1), Infinity];
  if (mode === "7d") return [day(-6), Infinity];
  if (mode === "30d") return [day(-29), Infinity];
  if (mode === "custom") {
    if (!from && !to) return null;
    const ymd = (s) => s.split("-").map(Number);
    const s = from ? tzMidnight(ymd(from)[0], ymd(from)[1] - 1, ymd(from)[2]) : -Infinity;
    const e = to ? tzMidnight(ymd(to)[0], ymd(to)[1] - 1, ymd(to)[2] + 1) : Infinity;
    return [s, e];
  }
  return null;
}

export const inCreatedRange = (lead, range) => {
  if (!range) return true;
  const t = lead?.createdAt ? new Date(lead.createdAt).getTime() : NaN;
  return !Number.isNaN(t) && t >= range[0] && t < range[1];
};

export function CreatedFilter({ value, onChange, zh, count, capped, className, modes = CREATED_MODES, testId = "crm-filter-created", unit, title }) {
  const active = !!createdRange(value);
  const label = modes.find((m) => m.id === value.mode);
  const anyLabel = modes[0]?.id === "" ? (zh ? modes[0].zh : modes[0].en) : (zh ? "创建时间：全部" : "Created: any");
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={testId} data-mode={value.mode || "all"} data-active={active ? 1 : 0}>
      <div className="relative">
        <CalendarDays className="h-3.5 w-3.5 absolute left-2 top-[11px] text-slate-400 pointer-events-none" />
        <select data-testid={`${testId}-mode`} className={`${className} pl-7 ${active ? "border-indigo-400 text-indigo-800 bg-indigo-50/60" : ""}`} value={value.mode} onChange={(e) => onChange({ mode: e.target.value, from: value.from, to: value.to })} title={title || (zh ? "按客户创建时间筛选" : "Filter by created date")}>
          {modes.map((m) => <option key={m.id} value={m.id}>{m.id ? (zh ? m.zh : m.en) : anyLabel}</option>)}
        </select>
      </div>
      {value.mode === "custom" && (
        <>
          <input type="date" data-testid={`${testId}-from`} className={`${className} !w-auto`} value={value.from} max={value.to || undefined} onChange={(e) => onChange({ ...value, from: e.target.value })} />
          <span className="text-slate-400 text-xs">→</span>
          <input type="date" data-testid={`${testId}-to`} className={`${className} !w-auto`} value={value.to} min={value.from || undefined} onChange={(e) => onChange({ ...value, to: e.target.value })} />
        </>
      )}
      {active && (
        <span className="inline-flex items-center gap-1 text-[11px] rounded-full bg-indigo-100 text-indigo-800 border border-indigo-200 px-2 py-0.5" data-testid={`${testId}-count`} data-count={count}>
          {value.mode === "custom" ? `${value.from || "…"} → ${value.to || "…"}` : (zh ? label?.zh : label?.en)} · {count}{capped ? "+" : ""} {unit || (zh ? "个客户" : "leads")}
          <button type="button" onClick={() => onChange({ mode: "", from: "", to: "" })} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 hover:text-red-600" data-testid={`${testId}-clear`} title={zh ? "清除" : "Clear"}><X className="h-3 w-3" /></button>
        </span>
      )}
    </div>
  );
}
